import type { Config } from "tailwindcss";
import tailwindcssAnimate from "tailwindcss-animate";

export default {
	darkMode: ["class"],
	content: [
		"./pages/**/*.{ts,tsx}",
		"./components/**/*.{ts,tsx}",
		"./app/**/*.{ts,tsx}",
		"./src/**/*.{ts,tsx}",
	],
	prefix: "",
	theme: {
		container: {
			center: true,
			padding: '2rem',
			screens: {
				'2xl': '1400px'
			}
		},
		extend: {
			colors: {
				border: 'hsl(var(--border))',
				input: 'hsl(var(--input))',
				ring: 'hsl(var(--ring))',
				background: 'hsl(var(--background))',
				foreground: 'hsl(var(--foreground))',
				primary: {
					DEFAULT: 'hsl(var(--primary))',
					foreground: 'hsl(var(--primary-foreground))'
				},
				secondary: {
					DEFAULT: 'hsl(var(--secondary))',
					foreground: 'hsl(var(--secondary-foreground))'
				},
				destructive: {
					DEFAULT: 'hsl(var(--destructive))',
					foreground: 'hsl(var(--destructive-foreground))'
				},
				muted: {
					DEFAULT: 'hsl(var(--muted))',
					foreground: 'hsl(var(--muted-foreground))'
				},
				accent: {
					DEFAULT: 'hsl(var(--accent))',
					foreground: 'hsl(var(--accent-foreground))'
				},
				popover: {
					DEFAULT: 'hsl(var(--popover))',
					foreground: 'hsl(var(--popover-foreground))'
				},
				card: {
					DEFAULT: 'hsl(var(--card))',
					foreground: 'hsl(var(--card-foreground))'
				},
				sidebar: {
					DEFAULT: 'hsl(var(--sidebar-background))',
					foreground: 'hsl(var(--sidebar-foreground))',
					primary: 'hsl(var(--sidebar-primary))',
					'primary-foreground': 'hsl(var(--sidebar-primary-foreground))',
					accent: 'hsl(var(--sidebar-accent))',
					'accent-foreground': 'hsl(var(--sidebar-accent-foreground))',
					border: 'hsl(var(--sidebar-border))',
					ring: 'hsl(var(--sidebar-ring))'
				},
				// Polaroids design tokens — use CSS custom properties for theme support
				cream: {
					DEFAULT: 'rgb(var(--pol-bg) / <alpha-value>)',
					dark: 'rgb(var(--pol-bg-dark) / <alpha-value>)',
				},
				'polaroid-white': 'rgb(var(--pol-white) / <alpha-value>)',
				'pol-pink': {
					DEFAULT: 'rgb(var(--pol-primary) / <alpha-value>)',
					glitter: 'rgb(var(--pol-primary-light) / <alpha-value>)',
				},
				'pol-lilac': '#C8A2D4',
				'pol-mint': '#A8E0D0',
				'pol-blue': '#A8D0E8',
				'pol-amber': '#E8C888',
				'pol-text': {
					DEFAULT: 'rgb(var(--pol-text-color) / <alpha-value>)',
					caption: 'rgb(var(--pol-text-caption-color) / <alpha-value>)',
					secondary: 'rgb(var(--pol-text-secondary-color) / <alpha-value>)',
				},
			},
			fontFamily: {
				sans: ['Outfit Variable', 'Outfit', 'system-ui', 'sans-serif'],
				logo: ['Pacifico', 'cursive'],
				hand: ['Caveat', 'cursive'],
			},
			borderRadius: {
				lg: 'var(--radius)',
				md: 'calc(var(--radius) - 2px)',
				sm: 'calc(var(--radius) - 4px)'
			},
			keyframes: {
				'accordion-down': {
					from: { height: '0' },
					to: { height: 'var(--radix-accordion-content-height)' }
				},
				'accordion-up': {
					from: { height: 'var(--radix-accordion-content-height)' },
					to: { height: '0' }
				},
				'fade-slide-up': {
					from: { opacity: '0', transform: 'translateY(24px)' },
					to: { opacity: '1', transform: 'translateY(0)' },
				},
				'doodle-float': {
					'0%, 100%': { transform: 'translateY(0)' },
					'50%': { transform: 'translateY(-2px)' },
				},
				'shimmer': {
					from: { backgroundPosition: '-200% 0' },
					to: { backgroundPosition: '200% 0' },
				},
				'glow-pulse': {
					'0%, 100%': { boxShadow: '0 0 12px rgba(232, 160, 191, 0.3)' },
					'50%': { boxShadow: '0 0 20px rgba(232, 160, 191, 0.6)' },
				},
				'flash': {
					'0%': { opacity: '0' },
					'30%': { opacity: '0.85' },
					'100%': { opacity: '0' },
				},
				'tape-sparkle': {
					'0%, 100%': { opacity: '0.4' },
					'50%': { opacity: '0.9' },
				},
				'develop-shimmer': {
					from: { left: '-100%' },
					to: { left: '200%' },
				},
			},
			animation: {
				'accordion-down': 'accordion-down 0.2s ease-out',
				'accordion-up': 'accordion-up 0.2s ease-out',
				'fade-slide-up': 'fade-slide-up 500ms cubic-bezier(0.16, 1, 0.3, 1) both',
				'doodle-float': 'doodle-float 3.5s ease-in-out infinite',
				'shimmer': 'shimmer 1.5s linear infinite',
				'glow-pulse': 'glow-pulse 2.5s ease-in-out infinite',
				'flash': 'flash 350ms ease-out forwards',
				'tape-sparkle': 'tape-sparkle 2s ease-in-out infinite',
				'develop-shimmer': 'develop-shimmer 3s linear infinite',
			},
		}
	},
	plugins: [tailwindcssAnimate],
} satisfies Config;
